/**
 * Where the app starts: error reporting, the check for a newer version, the
 * owner's Grow settings, the themes' colours and fonts from the database
 * (theme/themeTokens), where the visitor came from (a founding link, a
 * tagged link, a share's ?ref, a "Start a garden" link), the language of the
 * screens before an account (a Spanish browser gets Spanish), an old
 * home-screen icon sent to Today, and then the app itself inside the crash
 * screen's boundary.
 *
 * Used by: index.html, whose `<script type="module" src="/src/main.tsx">` is
 * the app's only entry (Vite builds it into dist/assets). Nothing imports it.
 * google-photos.html, the other page Vite builds, loads none of the app.
 *
 * Order matters here. Each call below runs before React renders anything,
 * and each is placed for a reason given beside it: error reporting first so
 * a crash in what follows is reported, the arrival kept before the router can
 * rewrite the address, and the start language set before the sign-in screen
 * draws. React.StrictMode renders twice in development only.
 *
 * Env vars: none read here directly; initSentry reads VITE_SENTRY_DSN,
 * VITE_SENTRY_ENVIRONMENT, VITE_SENTRY_TRACES_SAMPLE_RATE and
 * VITE_SENTRY_DEBUG (shared/lib/sentry), all baked into the public bundle;
 * shared/lib/analytics reads VITE_UMAMI_WEBSITE_ID and VITE_CLARITY_PROJECT_ID.
 *
 * Surprising: `window.__growStarted` and the session key `grow.shellRecovered`
 * are a contract with an inline script in index.html, not app state;
 * renaming either breaks the "could not finish loading" screen and the one-time
 * recovery for a deploy that removed the bundle.
 *
 * See private/docs-from-bloom-next/APP-ARCHITECTURE.md for the whole start-up path.
 *
 * @module main
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import App from './App';
import { initSentry, SentryErrorBoundary } from '@/shared/lib/sentry';
import { CrashScreen } from '@/shared/components/CrashScreen';
import { startUpdateChecks } from '@/shared/lib/pwaUpdate';
import { loadGrowSettings, rereadGrowSettingsWhenVisible } from '@/shared/lib/growSettings';
import { loadThemeTokens } from '@/shared/theme/themeTokens';
import { openOnHome } from '@/shared/lib/homePage';
import { keepArrival } from '@/shared/lib/arrivalSource';
import { applyStartLanguage } from '@/shared/lib/startLanguage';
import { initAnalytics } from '@/shared/lib/analytics';
import './style.css';

/*
 * A flag for the shell, set before anything that could throw.
 *
 * index.html watches for the case where this bundle never arrives at all —
 * a stale page asking for a file a deploy has removed — and says so rather
 * than leaving a white screen. This is how it knows to stay quiet: if the
 * bundle ran, whatever happened next is the app's problem and Sentry's, not
 * a missing file, and a route that renders nothing is not a broken page.
 */
(window as unknown as { __growStarted?: boolean }).__growStarted = true;

/*
 * And the shell's one recovery, given back.
 *
 * index.html lets itself drop the service worker and reload once per session
 * when this bundle fails to arrive, and remembers that in session storage so a
 * broken deploy cannot loop. Nothing ever cleared the mark, so an installed
 * app or a long-lived tab that had recovered once (kept open across a deploy)
 * had none left for the next deploy: a stale shell asked for a removed bundle,
 * the mark said "already tried", and the screen stayed blank or said "could
 * not finish loading" until the tab was closed. Reaching this line means the
 * bundle arrived, so the next failure is a new one and may recover again.
 */
try { window.sessionStorage.removeItem('grow.shellRecovered'); } catch { /* storage refused: nothing was marked */ }

// Before anything else renders, so the first thing that breaks is heard.
initSentry();

// Umami and Clarity, when their keys are set and this is a production build.
initAnalytics();

// An installed app is resumed, not navigated to, so it has to ask for itself.
startUpdateChecks();

// The owner's Grow settings; the defaults stand until they arrive.
void loadGrowSettings();

// And again when a tab left open becomes visible (B103), so a changed setting is heard.
rereadGrowSettingsWhenVisible();

// The themes' colours and fonts from the database (D61): the copy kept on this
// device at once (index.html painted it already), then the server's if it changed.
void loadThemeTokens();

// A founding code, a tagged link's values, a share's credit and a "Start a
// garden" link, before anything moves the address on: kept until sign-up
// (arrivalSource).
keepArrival();

// The sign-in page in the browser's language, or the one chosen there; an
// account's own preferences take over once it is signed in.
applyStartLanguage();

// An icon added to the home screen before Today was home still opens /plants.
openOnHome();

// #root is in index.html. Sentry's boundary sits outside App, so a render
// error anywhere (providers included) is reported and shows CrashScreen with
// Sentry's event id: the standard failure words and Reload, no Try again.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary
      fallback={({ eventId }) => (
        <CrashScreen eventId={eventId ?? null} />
      )}
    >
      <App />
    </SentryErrorBoundary>
  </React.StrictMode>
);
